import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, Send, Upload, FileText, CheckCircle2,
  Sparkles, ShoppingCart, Globe, Code, Smartphone, Briefcase,
  Loader2, Copy, X, Info, Repeat
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─────────────── Contextual help copy (Option C — Info icon + popover) ───────────────
// Friendly, jargon-light, ≤2 short sentences per field.
const HELP_TEXTS = {
  business_name: "Your registered or trading name. Goes on the quote PDF and the contract — make sure spelling is final.",
  business_type: "Pick the closest fit. We use this to pre-load smart defaults (e.g. e-commerce needs cart, SaaS needs auth).",
  business_overview: "A short paragraph about what you do, who buys from you, and the goal of this website. The more context, the sharper our quote.",
  project_type: "Landing page = single funnel page. Full website = multi-page (Home, About, Services…). Web app = logged-in dashboards & workflows. E-commerce = cart + checkout + inventory.",
  pages: "Rough count of distinct pages. Scope scales with pages — you can adjust during kick-off.",
  languages: "Multilingual means we set up the content + language switcher (and RTL if needed). Pick this only if you'll publish in 2+ languages.",
  timeline: "Rush (<2 weeks) gets priority queueing. Flexible (4–8 weeks) lets us batch with other projects.",
  features: "Optional add-ons that bolt onto your site. Skip what you don't need — easy to add post-launch.",
  commerce: "E-commerce essentials. Cart + inventory are the must-haves; coupons & GST invoicing matter for Indian SMBs.",
  sku_tier: "Total product count you'll list. 500+ unlocks bulk import tools, faster search & advanced filters.",
  payment_gateways: "Cashfree & Razorpay are best for India. Stripe is best for global cards. COD adds an offline confirm-call workflow.",
  seo_package: "No SEO = skip for now. Basic = on-page tags + sitemap + Search Console. Pro = + technical audit + 4 blog posts to start ranking faster.",
  marketing_addons: "Growth plug-ins. Analytics & email are usually worth it; performance opt becomes critical past 5+ pages.",
  branding: "Visual identity work. Tick anything where you don't already have professional assets — we handle the rest.",
  package_pick: "We blend your selections into 3 scope tiers (Lean / Recommended / Premium). Pick what fits — we'll send a custom quote within 24 hours.",
  contact_name: "Goes on the quote PDF & contract. Use the name of whoever signs invoices.",
  contact_email: "We email the branded PDF here. Also used for project status updates — make sure it's monitored.",
  contact_phone: "We only call at your chosen time slot. WhatsApp is welcome too.",
  contact_company: "Optional. Helps us address the quote to the right legal entity for GST invoicing.",
  contact_state: "Used for GST classification on the invoice (intra-state vs inter-state) and assigning your nearest project lead.",
  contact_city: "Helps if you're in one of our hubs and want an in-person kickoff. No pressure — most projects run fully remote.",
  contact_best_time: "We respect your schedule — we'll only call inside this window. Anytime = open all day.",
  contact_upload: "Drop a brief, wireframe, competitor list, or even rough sketches. Anything that speeds up scoping. Max 10 MB.",
  contact_notes: "Reference sites you love, hard budget limits, deal-breakers, or specific tech you want us to use.",
  contact_consent: "We only use your details to send this quote and follow up. No sharing with third parties. Unsubscribe anytime.",
  pricing_mode_toggle: "One-time = pay-and-own. Annual = build + hosting + SSL + domain + 6 updates + uptime SLA, all bundled.",
  auto_renew: "Auto-renews your plan after 12 months. You'll get a 14-day heads-up email before any charge — cancel anytime before that with one click. Off by default.",
  submit_action: "Submitting sends your requirement to our team. You'll get a custom quote on email within 24 hours.",
};

function FieldHelp({ slug, side = "top" }) {
  const text = HELP_TEXTS[slug];
  if (!text) return null;
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label="More info"
        data-testid={`field-help-${slug}`}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full text-zinc-500 hover:text-cyan-300 transition-colors focus:outline-none focus:text-cyan-300"
      >
        <Info size={14} />
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        className="w-72 bg-zinc-900 border-zinc-700 text-zinc-200 text-xs leading-relaxed p-3 shadow-xl shadow-black/40"
      >
        {text}
      </PopoverContent>
    </Popover>
  );
}

// Step config — keys MUST match backend PRICING_RULES
const PROJECT_TYPE_OPTS = [
  { key: "landing_page", label: "Landing page",  desc: "Single-page funnel · 1 to 3 sections", icon: Smartphone },
  { key: "full_website", label: "Full website",  desc: "Multi-page corporate / business site", icon: Briefcase },
  { key: "web_app",      label: "Web application", desc: "Logged-in dashboards, custom workflows", icon: Code },
  { key: "ecommerce",    label: "E-commerce store",  desc: "Cart, payments, inventory, orders",  icon: ShoppingCart },
];
const PAGE_OPTS = ["1", "3", "5", "10", "15+"];
const LANG_OPTS = [
  { key: "english",      label: "English only" },
  { key: "multilingual", label: "Multilingual" },
];
const TIMELINE_OPTS = [
  { key: "rush",     label: "Rush", sub: "<2 weeks" },
  { key: "standard", label: "Standard", sub: "2–4 weeks" },
  { key: "flexible", label: "Flexible", sub: "4–8 weeks" },
];
const FEATURE_OPTS = [
  { key: "contact_form",      label: "Contact form" },
  { key: "blog",              label: "Blog / News section" },
  { key: "newsletter",        label: "Newsletter signup" },
  { key: "live_chat",         label: "Live chat widget" },
  { key: "whatsapp_widget",   label: "WhatsApp click-to-chat" },
  { key: "booking_scheduler", label: "Booking / Scheduler" },
  { key: "search",            label: "Site search" },
  { key: "multi_language",    label: "Multi-language toggle" },
  { key: "member_login",      label: "Member login area" },
  { key: "admin_dashboard",   label: "Custom admin dashboard" },
  { key: "analytics_setup",   label: "Google Analytics setup" },
];
const COMMERCE_OPTS = [
  { key: "shopping_cart",   label: "Shopping cart" },
  { key: "inventory_mgmt",  label: "Inventory management" },
  { key: "shipping_calc",   label: "Shipping calculator" },
  { key: "coupons",         label: "Coupons & promos" },
  { key: "gst_invoicing",   label: "GST invoicing" },
];
const SKU_OPTS = ["≤50", "50–500", "500+"];
const GATEWAY_OPTS = [
  { key: "cashfree", label: "Cashfree" },
  { key: "razorpay", label: "Razorpay" },
  { key: "stripe",   label: "Stripe" },
  { key: "cod",      label: "Cash on delivery" },
];
const SEO_OPTS = [
  { key: "none",  label: "No SEO", sub: "Skip for now" },
  { key: "basic", label: "Basic SEO", sub: "On-page + sitemap" },
  { key: "pro",   label: "Pro SEO", sub: "+ technical + 4 blog posts" },
];
const MARKETING_OPTS = [
  { key: "analytics_setup",    label: "GA + Search Console setup" },
  { key: "social_integration", label: "Social media integration" },
  { key: "email_service",      label: "Email service (Resend/MC)" },
  { key: "performance_opt",    label: "Performance optimization" },
];
const BRANDING_OPTS = [
  { key: "logo_design",  label: "Logo design" },
  { key: "brand_kit",    label: "Brand colors + style guide" },
  { key: "copywriting",  label: "Copywriting (8–12 pages)" },
  { key: "stock_photos", label: "Stock photos / illustrations" },
  { key: "domain",       label: "Custom domain (1 yr)" },
];
const BUSINESS_TYPES = ["SaaS", "E-commerce", "Services", "Education", "Restaurant", "Real Estate", "Personal Brand", "Healthcare", "Non-profit", "Other"];

// India states + UTs (alphabetical) + "Outside India" catch-all for NRI / global leads
const INDIA_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
  "Outside India",
];

function fmtINR(n) {
  if (!n && n !== 0) return "—";
  return "₹" + Math.round(n).toLocaleString("en-IN");
}

const STEPS = [
  { id: 1, title: "Business", sub: "Tell us about you" },
  { id: 2, title: "Scope", sub: "Type & timeline" },
  { id: 3, title: "Features", sub: "What you need" },
  { id: 4, title: "Commerce", sub: "Cart & payments" },
  { id: 5, title: "Marketing", sub: "SEO & growth" },
  { id: 6, title: "Branding", sub: "Visual identity" },
  { id: 7, title: "Package", sub: "Pick your tier" },
  { id: 8, title: "Submit", sub: "Send & receive PDF" },
];

export default function QuoteWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState(null);
  const errorRef = useRef(null);
  const wizardRef = useRef(null);
  const [pricing, setPricing] = useState(null);
  const [uploadedDoc, setUploadedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [variants, setVariants] = useState(null);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);  // "basic" | "standard" | "premium"

  const [state, setState] = useState({
    business_name: "",
    business_type: "",
    business_overview: "",
    project_type: "",
    pages: "",
    languages: "english",
    timeline: "standard",
    features: ["contact_form"],
    commerce: [],
    sku_tier: "",
    payment_gateways: [],
    seo_package: "basic",
    marketing_addons: ["analytics_setup"],
    branding: [],
    pricing_mode: "one_time",       // "one_time" | "annual" (only for web_app/ecommerce)
    auto_renew_opt_in: false,       // shown on Step 8 when annual mode
  });
  const [contact, setContact] = useState({
    full_name: "",
    email: "",
    phone: "+91 ",
    company: "",
    state_region: "",
    city: "",
    best_time_to_reach: "Anytime",
    notes: "",
    consent: true,
  });

  const isCommerce = state.project_type === "ecommerce" || state.project_type === "web_app";
  const annualEligible = state.project_type === "web_app" || state.project_type === "ecommerce";
  const isAnnual = annualEligible && state.pricing_mode === "annual";

  // Live pricing (debounced)
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await axios.post(`${API}/quote/calculate`, { state });
        setPricing(r.data?.pricing || null);
      } catch (_) {
        setPricing(null);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [state]);

  // Fetch the 3 packages when we land on the Package step (or whenever state changes while on it)
  useEffect(() => {
    if (step !== 7) return;
    let cancelled = false;
    setVariantsLoading(true);
    axios.post(`${API}/quote/compare-packages`, { state })
      .then((r) => { if (!cancelled) { setVariants(r.data?.variants || null); } })
      .catch(() => { if (!cancelled) setVariants(null); })
      .finally(() => { if (!cancelled) setVariantsLoading(false); });
    return () => { cancelled = true; };
  }, [step, state]);

  function choosePackage(v) {
    setSelectedPackage(v.key);
    setState((s) => ({ ...s, ...v.state }));  // Apply the variant's state to wizard
  }

  function toggleArr(key, val) {
    setState((s) => {
      const arr = s[key] || [];
      return { ...s, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  }

  const stepValid = useMemo(() => {
    if (step === 1) return state.business_name.trim() && state.business_type && state.business_overview.trim().length >= 10;
    if (step === 2) return state.project_type && state.pages;
    if (step === 7) return !!selectedPackage;  // Must pick a package
    if (step === 8) {
      const phoneOk = (contact.phone.match(/\d/g) || []).length >= 10;
      return contact.full_name.trim()
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)
        && phoneOk
        && contact.state_region
        && contact.city.trim()
        && contact.consent;
    }
    return true;
  }, [step, state, contact, selectedPackage]);

  // Skip commerce step (4) when not e-com / web-app
  function next() {
    setErrorMsg("");
    let nx = step + 1;
    if (nx === 4 && !isCommerce) nx = 5;
    if (nx > 8) nx = 8;
    setStep(nx);
  }
  function back() {
    setErrorMsg("");
    let nx = step - 1;
    if (nx === 4 && !isCommerce) nx = 3;
    if (nx < 1) nx = 1;
    setStep(nx);
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setErrorMsg("File too large. Max 10 MB."); return; }
    setUploading(true);
    setErrorMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/quote/upload-doc`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (!r.data?.ok) throw new Error("Upload failed");
      setUploadedDoc({ doc_id: r.data.doc_id, filename: r.data.filename, size: r.data.size });
    } catch (e2) {
      setErrorMsg(e2?.response?.data?.detail || e2?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  // Scroll the error banner into view whenever a new error appears
  useEffect(() => {
    if (errorMsg && errorRef.current) {
      try { errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* no-op */ }
    }
  }, [errorMsg]);

  // Scroll back to top when the thank-you screen appears
  useEffect(() => {
    if (thankYou && wizardRef.current) {
      try { wizardRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); } catch { /* no-op */ }
    }
  }, [thankYou]);

  async function submit() {
    setSubmitting(true);
    setErrorMsg("");
    try {
      const payload = {
        state,
        ...contact,
        uploaded_doc_id: uploadedDoc?.doc_id || null,
        origin_url: window.location.origin,
      };
      const r = await axios.post(`${API}/quote/submit`, payload);
      if (!r.data?.ok) throw new Error("Submit failed");
      setThankYou(r.data);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e?.message || "Submission failed. Please retry or email support@shansoai.com if it keeps failing.");
    } finally {
      setSubmitting(false);
    }
  }

  // ────────────────────────────── THANK-YOU SCREEN ──────────────────────────────
  if (thankYou) {
    return (
      <div ref={wizardRef} className="max-w-2xl mx-auto bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-10" data-testid="quote-thank-you">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center shrink-0">
            <CheckCircle2 size={28} className="text-emerald-300" />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">Requirement received ✨</h3>
            <p className="text-zinc-400 mt-1 text-sm">Reference <code className="text-cyan-300 font-mono" data-testid="quote-ref-id">{thankYou.reference_id}</code></p>
          </div>
        </div>

        <div className="rounded-xl p-5 mb-6 border border-cyan-400/30" style={{ background: "linear-gradient(135deg, rgba(34,211,238,0.10), rgba(8,145,178,0.04))" }} data-testid="thankyou-next-steps">
          <p className="text-cyan-200 font-bold text-sm mb-1">📅 We&apos;ll send your custom quote within 24 hours</p>
          <p className="text-zinc-300 text-sm">Our team is reviewing your requirements right now. You&apos;ll receive a detailed quote tailored to your project on the email below.</p>
        </div>

        <div className="text-sm text-zinc-400 space-y-2">
          <p>📧 Confirmation sent to <b className="text-white">{contact.email}</b></p>
          <p>💬 Have questions in the meantime? Write to <a href="mailto:support@shansoai.com" className="text-cyan-300">support@shansoai.com</a></p>
        </div>

        <button onClick={onClose} className="mt-6 text-zinc-500 text-xs hover:text-white" data-testid="close-thankyou">Close</button>
      </div>
    );
  }

  // ────────────────────────────── WIZARD ──────────────────────────────
  return (
    <div ref={wizardRef} className="max-w-3xl mx-auto" data-testid="quote-wizard">
      <div>
        {/* Header strip with close (sidebar removed — pure requirement-collection wizard) */}
        {onClose && (
          <div className="flex items-center justify-end mb-2">
            <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-wizard" aria-label="Close"><X size={18}/></button>
          </div>
        )}

        {/* Main column — progress + form */}
        <div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const visible = !(s.id === 4 && !isCommerce);
          if (!visible) return null;
          const active = step === s.id;
          const done = step > s.id;
          return (
            <div key={s.id} className="flex items-center gap-2 shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${
                active ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" :
                done ? "bg-emerald-500/10 border-emerald-500/40 text-emerald-300" :
                "bg-zinc-900 border-zinc-800 text-zinc-500"
              }`}>
                {done ? <CheckCircle2 size={12}/> : <span>{s.id}</span>}
                <span className="hidden sm:inline">{s.title}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`h-px w-6 ${done ? "bg-emerald-500/40" : "bg-zinc-800"}`}/>}
            </div>
          );
        })}
      </div>

      <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6 sm:p-8">
        <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">{STEPS[step-1].title}</h3>
        <p className="text-zinc-400 text-sm mb-6">{STEPS[step-1].sub}</p>

        <div className="max-h-[55vh] sm:max-h-[60vh] overflow-y-auto pr-2 -mr-2 quote-wizard-scroll" data-testid="wizard-scroll-area">
        <style>{`
          .quote-wizard-scroll::-webkit-scrollbar { width: 6px; }
          .quote-wizard-scroll::-webkit-scrollbar-track { background: transparent; }
          .quote-wizard-scroll::-webkit-scrollbar-thumb { background: rgba(82,82,91,0.6); border-radius: 3px; }
          .quote-wizard-scroll::-webkit-scrollbar-thumb:hover { background: rgba(82,82,91,0.9); }
        `}</style>

        {/* STEP 1 — BUSINESS */}
        {step === 1 && (
          <div className="space-y-5" data-testid="step-1">
            <Field label="Business name *" testid="business-name-input" helpKey="business_name">
              <input type="text" value={state.business_name} onChange={(e)=>setState(s=>({...s, business_name:e.target.value}))} placeholder="Acme Solutions Pvt. Ltd." className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none" />
            </Field>
            <Field label="Business type *" helpKey="business_type">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {BUSINESS_TYPES.map((t)=>(
                  <button key={t} type="button" onClick={()=>setState(s=>({...s, business_type:t}))} data-testid={`biz-type-${t}`}
                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${state.business_type===t ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Business overview * (10+ chars)" testid="business-overview-input" helpKey="business_overview">
              <textarea rows={4} value={state.business_overview} onChange={(e)=>setState(s=>({...s, business_overview:e.target.value}))} placeholder="What does your business do? Who's your customer? What's the goal of this website?" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none focus:border-cyan-400 focus:outline-none" />
            </Field>
          </div>
        )}

        {/* STEP 2 — SCOPE */}
        {step === 2 && (
          <div className="space-y-6" data-testid="step-2">
            <Field label="Project type *" helpKey="project_type">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PROJECT_TYPE_OPTS.map(({key,label,desc,icon:Ic})=>(
                  <button key={key} type="button" onClick={()=>setState(s=>({...s, project_type:key}))} data-testid={`project-type-${key}`}
                    className={`text-left px-4 py-4 rounded-lg border transition-colors ${state.project_type===key ? "bg-cyan-500/10 border-cyan-400" : "bg-zinc-800 border-zinc-700 hover:border-zinc-500"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Ic size={16} className={state.project_type===key ? "text-cyan-300" : "text-zinc-400"}/>
                      <p className={`text-sm font-bold ${state.project_type===key ? "text-cyan-200" : "text-white"}`}>{label}</p>
                    </div>
                    <p className="text-xs text-zinc-400">{desc}</p>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Approx. number of pages *" helpKey="pages">
              <div className="grid grid-cols-5 gap-2">
                {PAGE_OPTS.map((p)=>(
                  <button key={p} type="button" onClick={()=>setState(s=>({...s, pages:p}))} data-testid={`pages-${p}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-bold ${state.pages===p ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{p}</button>
                ))}
              </div>
            </Field>
            <Field label="Languages" helpKey="languages">
              <div className="grid grid-cols-2 gap-2">
                {LANG_OPTS.map(({key,label})=>(
                  <button key={key} type="button" onClick={()=>setState(s=>({...s, languages:key}))} data-testid={`lang-${key}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-medium ${state.languages===key ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{label}</button>
                ))}
              </div>
            </Field>
            <Field label="Timeline" helpKey="timeline">
              <div className="grid grid-cols-3 gap-2">
                {TIMELINE_OPTS.map(({key,label,sub})=>(
                  <button key={key} type="button" onClick={()=>setState(s=>({...s, timeline:key}))} data-testid={`timeline-${key}`}
                    className={`text-left px-3 py-2.5 rounded-lg border ${state.timeline===key ? "bg-cyan-500/15 border-cyan-400" : "bg-zinc-800 border-zinc-700 hover:border-zinc-500"}`}>
                    <p className={`text-sm font-bold ${state.timeline===key ? "text-cyan-200" : "text-white"}`}>{label}</p>
                    <p className="text-[11px] text-zinc-400">{sub}</p>
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {/* STEP 3 — FEATURES */}
        {step === 3 && (
          <div data-testid="step-3">
            <div className="flex items-center gap-1.5 mb-3">
              <p className="text-zinc-400 text-sm">Pick everything you need. Skip the rest — you can always add later.</p>
              <FieldHelp slug="features" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FEATURE_OPTS.map(({key,label})=>{
                const on = state.features.includes(key);
                return (
                  <button key={key} type="button" onClick={()=>toggleArr("features", key)} data-testid={`feature-${key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left ${on ? "bg-cyan-500/10 border-cyan-400 text-cyan-100" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-cyan-400 border-cyan-400" : "border-zinc-500"}`}>
                      {on && <CheckCircle2 size={12} className="text-black"/>}
                    </span>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4 — COMMERCE */}
        {step === 4 && (
          <div className="space-y-5" data-testid="step-4">
            <Field label="Commerce features" helpKey="commerce">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {COMMERCE_OPTS.map(({key,label})=>{
                  const on = state.commerce.includes(key);
                  return (
                    <button key={key} type="button" onClick={()=>toggleArr("commerce", key)} data-testid={`commerce-${key}`}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left ${on ? "bg-cyan-500/10 border-cyan-400 text-cyan-100" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-cyan-400 border-cyan-400" : "border-zinc-500"}`}>{on && <CheckCircle2 size={12} className="text-black"/>}</span>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="SKU count" helpKey="sku_tier">
              <div className="grid grid-cols-3 gap-2">
                {SKU_OPTS.map((p)=>(
                  <button key={p} type="button" onClick={()=>setState(s=>({...s, sku_tier:p}))} data-testid={`sku-${p}`}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-bold ${state.sku_tier===p ? "bg-cyan-500/15 border-cyan-400 text-cyan-200" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{p}</button>
                ))}
              </div>
            </Field>
            <Field label="Payment gateways" helpKey="payment_gateways">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {GATEWAY_OPTS.map(({key,label})=>{
                  const on = state.payment_gateways.includes(key);
                  return (
                    <button key={key} type="button" onClick={()=>toggleArr("payment_gateways", key)} data-testid={`gateway-${key}`}
                      className={`px-3 py-2.5 rounded-lg border text-sm font-medium ${on ? "bg-cyan-500/10 border-cyan-400 text-cyan-100" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{label}</button>
                  );
                })}
              </div>
            </Field>
          </div>
        )}

        {/* STEP 5 — MARKETING */}
        {step === 5 && (
          <div className="space-y-5" data-testid="step-5">
            <Field label="SEO package" helpKey="seo_package">
              <div className="grid grid-cols-3 gap-2">
                {SEO_OPTS.map(({key,label,sub})=>(
                  <button key={key} type="button" onClick={()=>setState(s=>({...s, seo_package:key}))} data-testid={`seo-${key}`}
                    className={`text-left px-3 py-2.5 rounded-lg border ${state.seo_package===key ? "bg-cyan-500/15 border-cyan-400" : "bg-zinc-800 border-zinc-700 hover:border-zinc-500"}`}>
                    <p className={`text-sm font-bold ${state.seo_package===key ? "text-cyan-200" : "text-white"}`}>{label}</p>
                    <p className="text-[11px] text-zinc-400">{sub}</p>
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Marketing add-ons" helpKey="marketing_addons">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MARKETING_OPTS.map(({key,label})=>{
                  const on = state.marketing_addons.includes(key);
                  return (
                    <button key={key} type="button" onClick={()=>toggleArr("marketing_addons", key)} data-testid={`marketing-${key}`}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left ${on ? "bg-cyan-500/10 border-cyan-400 text-cyan-100" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                      <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-cyan-400 border-cyan-400" : "border-zinc-500"}`}>{on && <CheckCircle2 size={12} className="text-black"/>}</span>
                      {label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </div>
        )}

        {/* STEP 6 — BRANDING */}
        {step === 6 && (
          <div data-testid="step-6">
            <div className="flex items-center gap-1.5 mb-3">
              <p className="text-zinc-400 text-sm">Need help with the visual identity? Pick what applies.</p>
              <FieldHelp slug="branding" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {BRANDING_OPTS.map(({key,label})=>{
                const on = state.branding.includes(key);
                return (
                  <button key={key} type="button" onClick={()=>toggleArr("branding", key)} data-testid={`branding-${key}`}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm text-left ${on ? "bg-cyan-500/10 border-cyan-400 text-cyan-100" : "bg-zinc-800 border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-cyan-400 border-cyan-400" : "border-zinc-500"}`}>{on && <CheckCircle2 size={12} className="text-black"/>}</span>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 7 — PACKAGE COMPARE (pricing hidden — feature-only) */}
        {step === 7 && (
          <div data-testid="step-7-package">
            <div className="flex items-center gap-1.5 mb-5">
              <p className="text-zinc-400 text-sm">Pick the scope that fits your project. Our team will send a custom quote based on your selection within 24 hours.</p>
              <FieldHelp slug="package_pick" />
            </div>

            {variantsLoading || !variants ? (
              <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin text-zinc-500"/></div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {variants.map((v) => {
                  const active = selectedPackage === v.key;
                  const accent = v.key === "basic" ? "zinc" : v.key === "premium" ? "violet" : "cyan";
                  const ringColor = accent === "cyan" ? "border-cyan-400" : accent === "violet" ? "border-violet-400" : "border-zinc-600";
                  const bgGlow = accent === "cyan" ? "rgba(34,211,238,0.08)" : accent === "violet" ? "rgba(167,139,250,0.08)" : "rgba(82,82,91,0.06)";
                  const numColor = accent === "cyan" ? "text-cyan-300" : accent === "violet" ? "text-violet-300" : "text-zinc-200";
                  return (
                    <button key={v.key} type="button" onClick={()=>choosePackage(v)} data-testid={`package-${v.key}`}
                      className={`relative text-left rounded-2xl border-2 px-5 pt-6 pb-5 transition-all ${active ? ringColor + " scale-[1.02]" : "border-zinc-800 hover:border-zinc-600"} ${v.recommended ? "lg:-translate-y-2" : ""}`}
                      style={{ background: `linear-gradient(160deg, ${bgGlow}, rgba(24,24,27,0.6) 70%)` }}>
                      {v.recommended && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-cyan-400 text-black" data-testid="most-popular-badge">
                          ★ Most popular
                        </span>
                      )}
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white text-lg font-bold">{v.name}</p>
                        {active && <CheckCircle2 size={18} className="text-emerald-400"/>}
                      </div>
                      <p className="text-zinc-500 text-xs mb-5">{v.tagline}</p>

                      <ul className="space-y-1.5 mb-5">
                        {v.highlights.map((h, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-snug">
                            <CheckCircle2 size={12} className={`shrink-0 mt-0.5 ${numColor}`}/>
                            <span className="truncate">{h}</span>
                          </li>
                        ))}
                        {v.pricing.line_items.length > v.highlights.length && (
                          <li className="text-[11px] text-zinc-600 pl-5">+ {v.pricing.line_items.length - v.highlights.length} more</li>
                        )}
                      </ul>
                      <div className={`text-center text-xs font-bold py-2 rounded-lg ${active ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/40" : "bg-zinc-800/60 text-zinc-400 border border-zinc-700"}`}>
                        {active ? "✓ Selected" : "Choose this →"}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {selectedPackage && (
              <p className="text-emerald-300 text-xs mt-4 text-center">
                Nice pick. Click <b>Continue</b> to add your contact info and request your quote.
              </p>
            )}
          </div>
        )}

        {/* STEP 8 — SUBMIT */}
        {step === 8 && (
          <div className="space-y-4" data-testid="step-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name *" testid="contact-name" helpKey="contact_name">
                <input type="text" value={contact.full_name} onChange={(e)=>setContact(c=>({...c, full_name:e.target.value}))} placeholder="Your name" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none" />
              </Field>
              <Field label="Email *" testid="contact-email" helpKey="contact_email">
                <input type="email" value={contact.email} onChange={(e)=>setContact(c=>({...c, email:e.target.value}))} placeholder="you@business.com" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none" />
              </Field>
              <Field label="Phone (with country code) *" testid="contact-phone" helpKey="contact_phone">
                <input
                  type="tel"
                  value={contact.phone}
                  onChange={(e)=>setContact(c=>({...c, phone:e.target.value}))}
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none"
                  data-testid="contact-phone-input"
                />
              </Field>
              <Field label="Company (optional)" testid="contact-company" helpKey="contact_company">
                <input type="text" value={contact.company} onChange={(e)=>setContact(c=>({...c, company:e.target.value}))} placeholder="Your company" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none" />
              </Field>
              <Field label="State / region *" testid="contact-state" helpKey="contact_state">
                <select value={contact.state_region} onChange={(e)=>setContact(c=>({...c, state_region:e.target.value}))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none">
                  <option value="">Select state…</option>
                  {INDIA_STATES.map((s)=> <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="City *" testid="contact-city" helpKey="contact_city">
                <input type="text" value={contact.city} onChange={(e)=>setContact(c=>({...c, city:e.target.value}))} placeholder="e.g. Bangalore" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none" />
              </Field>
              <Field label="Best time to reach" helpKey="contact_best_time">
                <select value={contact.best_time_to_reach} onChange={(e)=>setContact(c=>({...c, best_time_to_reach:e.target.value}))} className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-cyan-400 focus:outline-none">
                  {["Anytime","Morning (9–12)","Afternoon (12–4)","Evening (4–8)"].map(o=><option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Upload requirement doc (optional, ≤10 MB)" helpKey="contact_upload">
                <div className="flex items-center gap-2">
                  <label className="flex-1 cursor-pointer flex items-center justify-center gap-2 px-4 py-3 bg-zinc-800 border border-zinc-700 border-dashed rounded-lg text-zinc-300 hover:border-cyan-400 hover:text-cyan-200" data-testid="upload-doc-label">
                    {uploading ? <Loader2 size={16} className="animate-spin"/> : <Upload size={16}/>}
                    <span className="text-sm">{uploading ? "Uploading…" : uploadedDoc ? `📎 ${uploadedDoc.filename}` : "Choose file (PDF / DOC / PNG)"}</span>
                    <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt" onChange={handleFileChange} className="hidden" data-testid="upload-doc-input"/>
                  </label>
                  {uploadedDoc && <button type="button" onClick={()=>setUploadedDoc(null)} className="text-zinc-500 hover:text-red-400" data-testid="clear-upload"><X size={16}/></button>}
                </div>
              </Field>
            </div>
            <Field label="Notes (anything else we should know?)" testid="contact-notes" helpKey="contact_notes">
              <textarea rows={3} value={contact.notes} onChange={(e)=>setContact(c=>({...c, notes:e.target.value}))} placeholder="Inspirations, competitor sites, budget constraints, anything…" className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none focus:border-cyan-400 focus:outline-none" />
            </Field>
            <label className="flex items-start gap-2 text-zinc-400 text-xs cursor-pointer" data-testid="consent-checkbox">
              <input type="checkbox" checked={contact.consent} onChange={(e)=>setContact(c=>({...c, consent:e.target.checked}))} className="mt-0.5"/>
              <span className="flex-1">I agree to ShansoAI reaching out via email/phone about this quote. We don&apos;t share data with third parties.</span>
              <span className="mt-0.5"><FieldHelp slug="contact_consent" /></span>
            </label>
          </div>
        )}

        </div>
      </div>

      {/* Persistent error banner (outside scroll body so always visible after submit) */}
      {errorMsg && (
        <div ref={errorRef} className="mt-4 px-4 py-3 rounded-xl bg-red-500/15 border border-red-400/40 text-red-200 text-sm flex items-start gap-2" data-testid="wizard-error" role="alert">
          <span className="shrink-0">⚠</span>
          <span className="flex-1">{errorMsg}</span>
          <button type="button" onClick={()=>setErrorMsg("")} className="text-red-300 hover:text-red-100" aria-label="Dismiss"><X size={14}/></button>
        </div>
      )}

      {/* Nav */}
      <div className="flex items-center justify-between gap-3 mt-6">
        <button onClick={back} disabled={step===1} data-testid="wizard-back-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed">
          <ChevronLeft size={16}/> Back
        </button>
        {step < 8 ? (
          <button onClick={next} disabled={!stepValid} data-testid="wizard-next-btn"
            className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-500 text-black font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
            Continue <ChevronRight size={16}/>
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <FieldHelp slug="submit_action" side="top" />
            <button onClick={submit} disabled={!stepValid || submitting} data-testid="wizard-submit-btn"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-lg bg-gradient-to-r from-cyan-400 to-cyan-500 text-black font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed">
              {submitting ? <><Loader2 size={16} className="animate-spin"/> Sending…</> : <>Send my quote <Send size={16}/></>}
            </button>
          </div>
        )}
      </div>

        </div> {/* end main column */}
      </div> {/* end grid */}
    </div>
  );
}

function Field({ label, children, testid, helpKey }) {
  return (
    <div data-testid={testid}>
      <label className="flex items-center gap-1.5 text-sm font-bold text-zinc-200 mb-2">
        <span>{label}</span>
        {helpKey && <FieldHelp slug={helpKey} />}
      </label>
      {children}
    </div>
  );
}
